import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import cn from "clsx";
import { useAppDispatch, useAppSelector } from "store";

import styles from "../CmsFooterPaymentsEditModal.module.scss";
import InnerInput from "components/inputs/InnerInput/InnerInput";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import SwitchBtn from "components/buttons/SwitchBtn";
import { DraggableList } from "components/DraggableList/DraggableList";
import RoundBtn from "components/buttons/RoundBtn";
import { toggleArray } from "helpers/toggleArray";

import {
  handleUpdateDraftItem as onUpdateDraftItem,
  type PaymentMethod,
  PaymentExtended,
} from "store/cms/footer/payments/slice";
import { selectFilteredGeo } from "store/geoFilter/selectors";
import { getGeoDropdownOptions } from "helpers/geoDropdown";

type Props = {
  fieldErrors?: Record<string, string>;
  clearFieldError: (field: string) => void;
  readOnly?: boolean;
};

const PaymentsMainTab: React.FC<Props> = ({
  fieldErrors,
  clearFieldError,
  readOnly,
}) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const geoList = useAppSelector(selectFilteredGeo);

  const draftItem = useAppSelector((store) => store["cms-payments"].draftItem);
  const paymentMethodsListGlobal = useAppSelector(
    (store) => store["payment-methods"].list,
  );

  const [availablePaymentMethodsSelected, setAvailablePaymentMethodsSelected] =
    useState<PaymentMethod["id"][]>([]);

  const [connectedPaymentMethodsSelected, setConnectedPaymentMethodsSelected] =
    useState<PaymentMethod["id"][]>([]);

  const handleChangeData = (data: Partial<PaymentExtended>) => {
    dispatch(onUpdateDraftItem(data));
  };

  const onSelectAvailablePaymentMethod = (id: PaymentMethod["id"]) => {
    setAvailablePaymentMethodsSelected((c) => toggleArray(c, id));
    setConnectedPaymentMethodsSelected([]);
  };

  const onSelectConnectedPaymentMethod = (id: PaymentMethod["id"]) => {
    setConnectedPaymentMethodsSelected((c) => toggleArray(c, id));
    setAvailablePaymentMethodsSelected([]);
  };

  const onSortConnectedPaymentMethod = (sorted: PaymentMethod[]) => {
    handleChangeData({ payments: sorted });
  };

  const onAddPaymentMethod = (paymentIds: number[]) => {
    const newPaymentsToAdd = paymentMethodsListGlobal
      .filter((p) => paymentIds.includes(p.id as number))
      .map((p) => ({
        id: p.id as number,
        title: p.title,
        image: p.image,
      })) as PaymentExtended["payments"];

    handleChangeData({
      payments: [...draftItem.payments, ...newPaymentsToAdd],
    });

    setAvailablePaymentMethodsSelected([]);
  };

  const onDeletePaymentMethod = (paymentIds: number[]) => {
    handleChangeData({
      payments: draftItem.payments.filter((p) => !paymentIds.includes(p.id)),
    });
    setConnectedPaymentMethodsSelected([]);
  };

  const availablePaymentMethods = paymentMethodsListGlobal
    .map((p) => ({
      id: p.id,
      title: p.title,
      image: p.image,
    }))
    .filter((p) => !draftItem.payments.some((a) => a.id === p.id));

  return (
    <div className={styles.content}>
      <div className={styles.action}>
        <div className={cn(styles.actionItem)}>
          <InnerInput
            value={draftItem?.title ?? ""}
            placeholder={t("enterTitle")}
            label={t("title")}
            type="text"
            id="text-main-title"
            onChange={(e) => {
              handleChangeData({ title: e });
              clearFieldError("title");
            }}
            hasError={Boolean(fieldErrors?.["title"])}
            disabled={readOnly}
          />
        </div>

        <div className={cn(styles.actionItem, styles.geoActionItem)}>
          <DefaultDropdown
            options={getGeoDropdownOptions(geoList)}
            label={t("geo")}
            placeholder="GEO"
            value={draftItem?.geo || geoList[0]}
            onSelect={(t) => {
              handleChangeData({ geo: `${t}` });
              clearFieldError("geo");
            }}
            hasError={Boolean(fieldErrors?.["geo"])}
            disabled={readOnly}
          />
        </div>

        <div className={cn(styles.actionItem)}>
          <SwitchBtn
            activeTxt={t("active")}
            isActive={!!draftItem?.isActive}
            handleClick={() =>
              handleChangeData({
                isActive: draftItem?.isActive ? 0 : 1,
              })
            }
            disabled={readOnly}
          />
        </div>
      </div>

      <div className={styles.paymentsContent}>
        <div className={styles.draggableListWrapper}>
          <p className={styles.draggableListTitle}>{t("selected")}</p>

          <DraggableList
            // @ts-ignore
            items={draftItem.payments}
            selectedItems={connectedPaymentMethodsSelected}
            onSelect={onSelectConnectedPaymentMethod}
            onSort={onSortConnectedPaymentMethod}
            isDragRestricted={readOnly}
          >
            {draftItem.payments.map((item, idx) => (
              <DraggableList.Item
                key={item.id}
                // @ts-ignore
                id={item.id}
                index={idx}
                className={styles.dndItem}
              >
                <div className={styles.dndItem}>{item.title}</div>
              </DraggableList.Item>
            ))}
          </DraggableList>
        </div>

        {!readOnly && (
          <div className={styles.addRemoveButtons}>
            <RoundBtn
              handleClick={() =>
                onAddPaymentMethod(availablePaymentMethodsSelected)
              }
              txt="+"
              disabled={!availablePaymentMethodsSelected.length}
            />
            <RoundBtn
              handleClick={() =>
                onDeletePaymentMethod(connectedPaymentMethodsSelected)
              }
              txt="-"
              disabled={!connectedPaymentMethodsSelected.length}
            />
          </div>
        )}

        <div className={styles.draggableListWrapper}>
          <p className={styles.draggableListTitle}>{t("available")}</p>

          <DraggableList
            // @ts-ignore
            items={availablePaymentMethods}
            selectedItems={availablePaymentMethodsSelected}
            onSelect={(v) => onSelectAvailablePaymentMethod(+v)}
            isDragRestricted
          >
            {availablePaymentMethods.map((item, idx) => (
              <DraggableList.Item
                key={item.id}
                // @ts-ignore
                id={item.id}
                index={idx}
                className={styles.dndItem}
              >
                <div className={styles.dndItem}>{item.title}</div>
              </DraggableList.Item>
            ))}
          </DraggableList>
        </div>
      </div>
    </div>
  );
};

export default PaymentsMainTab;
